import { useContext, lazy } from "react";
import { ChunkTrackerContext } from "./context";

/**
 * TrackableChunk component that tracks the presence of a chunk using React context.
 *
 * @param {Object} props
 * @param {String} props.chunkId - The unique identifier (name) for the chunk.
 * @param {React.ReactNode} props.children - The child components to be rendered.
 * @returns {React.ReactNode} The rendered children components.
 */
const TrackableChunk = ({ chunkId, children }) => {
    const chunkTracker = useContext(ChunkTrackerContext);

    if (!chunkTracker?.hasChunk?.(chunkId)) {
        chunkTracker?.addChunk(chunkId);
    }

    return children;
};

/**
 * Creates a lazily loaded component that tracks its chunk. This enables preloading necessary chunks server-side to improve page load.
 *
 * @param {Function} importFunc - The dynamic import function for React.lazy.
 * @param {String} chunkId - The id of the chunk to track.
 * @returns {Function} The tracked lazy component.
 */
export const createTrackedLazyComponent = (importFunc, chunkId) => {
    const LazyComponent = lazy(importFunc);

    if (!chunkId) {
        throw new Error(
            "chunkId is required for the component chunk tracking to work properly"
        );
    }

    return (props) => (
        <TrackableChunk chunkId={chunkId}>
            <LazyComponent {...props} />
        </TrackableChunk>
    );
};

export default TrackableChunk;
